import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { cloneDeep, isEmpty } from "lodash";
import {
  ResponsiveContainer,
  BarChart,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Bar,
  Legend,
  Tooltip,
} from "recharts";

// import 'Styles/commonStyles.scss';
import OpennessImage from "../../Assets/images/reports/openness.png";
import AgreeablenessImage from "../../Assets/images/reports/agreeable.png";
import NeuroticismImage from "../../Assets/images/reports/neuroticism.png";
import ExtraversionImage from "../../Assets/images/reports/extraversion.png";
import BigFiveBannerImage from "../../Assets/images/reports/bigFiveBanner.png";
import ConscientiousnessImage from "../../Assets/images/reports/conscientiousness.png";
import { useGetPersonalityInterviewReport } from "../../Hooks/AssesmentHooks";
import { BiArrowBack } from "react-icons/bi";
import { Button } from "antd";

const backgroundImages = [
  OpennessImage,
  ConscientiousnessImage,
  ExtraversionImage,
  AgreeablenessImage,
  NeuroticismImage,
];

const CHART_TYPES = {
  AGR: "Agreeableness",
  CON: "Conscientiousness",
  EXT: "Extraversion",
  NEU: "Neuroticism",
  OPN: "Openness",
};

const CHART_COLORS = ["#FFD600", "#E8A8FF", "#FF9292", "#7CF5AC", "#90EBFF"];

export default function PreviewPersonality(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reportdata, setReportData] = useState();
  // const interviewId = 6301;

  const { data: report, isLoading: isReportLoading } =
    useGetPersonalityInterviewReport({
      params: {
        id: id,
      },
    });

  const results = cloneDeep(report)?.personality_result;

  useEffect(() => {
    setReportData(report);
  }, [report]);
  delete results?.descp;

  const assessment = reportdata?.personality_result?.descp;

  const chartValues = useMemo(() => {
    if (!isEmpty(results)) {
      const values = Object.entries(results).map(([key, value]) => ({
        name: CHART_TYPES[key],
        metric: value,
      }));

      return values;
    }
  }, [results]);

  return (
    <>
      <div>
        <div
          className={`pb-6 max-h-[calc(90vh-4rem)] bg-white overflow-y-auto customScrollBar`}
        >
          
          <div
            style={{
              backgroundImage: `url(${BigFiveBannerImage})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            className="h-[21rem] flex flex-col justify-center items-center relative"
          >
            <Button
            type="ghost"
            className="flex items-center px-0 font-bold cursor-pointer absolute top-4 left-4 text-black"
            onClick={() => {
              navigate(-1);
            }}
          >
            <BiArrowBack />
            <span className="ml-2">Back</span>
          </Button>
            <h1 className="pb-3 text-3xl font-extrabold text-center text-blue-950">
              The Big Five Personality Test
            </h1>
            <div className="text-base font-semibold text-black">
              Results For {reportdata?.candidate_name ?? "Candidate"}
            </div>
            {/* <div className="text-[#434343] text-base font-light">February 23, 2024, 6:15 PM</div> */}
          </div>

          <section className="flex flex-col items-center justify-center">
            <div className="pt-14 xs:w-10/12 md:w-[40rem] text-center">
              <h2 className="text-2xl font-bold text-center text-blue-950">
                Your Personality Trait Score
              </h2>
              <div className=" text-[#434343] text-sm font-medium text-justify">
                It is important to note that each of the five primary
                personality traits represents a range between two extremes. For
                example, extraversion represents a continuum between extreme
                extraversion and extreme introversion. In the real world, most
                people lie somewhere in between.
              </div>
            </div>
            {!isEmpty(chartValues) && (
              <div className="py-10 xs:w-11/12 md:w-[46rem] border-b border-b-[#E6EAF8] flex justify-center items-center">
                <ResponsiveContainer width={"70%"} height={300}>
                  <BarChart data={chartValues} barCategoryGap={9}>
                    <CartesianGrid strokeDasharray="4 0" />
                    <XAxis
                      axisLine
                      strokeWidth={0}
                      tick={false}
                      tickLine
                      dataKey={"name"}
                    />
                    <YAxis
                      domain={[0, 100]}
                      axisLine
                      strokeWidth={0}
                      tick
                      tickCount={10}
                      tickLine
                    />
                    <Tooltip cursor={{ fill: "none" }} />
                    <Bar fill="#D1D605" dataKey="metric">
                      {chartValues.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index]}
                        />
                      ))}
                    </Bar>
                    <Legend
                      verticalAlign="bottom"
                      align="left"
                      iconType="square"
                      iconSize={10}
                      layout="horizontal"
                      wrapperStyle={{
                        //paddingLeft: '25px',
                        bottom: -15,
                        left: 45,
                      }}
                      payload={[
                        {
                          value: "Agreeableness",
                          type: "square",
                          color: "#FAE52C ",
                        },
                        {
                          value: "Conscientiousness",
                          type: "square",
                          color: "#D680FF",
                        },
                        {
                          value: "Extraversion",
                          type: "square",
                          color: "#FF9292",
                        },
                        {
                          value: "Neuroticism",
                          type: "square",
                          color: "#7CF5AC",
                        },
                        {
                          value: "Openness",
                          type: "square",
                          color: "#90EBFF",
                        },
                      ]}
                      formatter={(value) => (
                        <span className="ml-2 text-black">{value}</span>
                      )}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {!isEmpty(assessment) &&
              assessment.map((field, index) => (
                <div
                  key={`assessment-item-${index}`}
                  className={`py-10 xs:w-11/12 md:w-[46rem] border-b border-b-[#E6EAF8] flex xs:flex-col ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  } justify-center items-center gap-x-20`}
                >
                  <div
                    style={{
                      backgroundImage: `url(${backgroundImages[index]})`,
                    }}
                    className="flex flex-col items-center justify-center px-2 text-center bg-center bg-no-repeat bg-contain xs:py-4 md:py-14"
                  >
                    <h3 className="text-xl font-bold text-blue-950">
                      {field?.label}
                    </h3>
                    {field?.score && (
                      <div className="text-[#00B165] font-semibold text-base w-44">
                        score: {field?.score}%
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-center justify-center gap-4">
                    {field?.key_points && (
                      <p className="text-[#434343] text-sm font-semibold">
                        Key Points: {field?.key_points}
                      </p>
                    )}
                    <p className="text-[#434343] text-sm font-medium text-justify">
                      {field?.report_for_hr}
                    </p>
                  </div>
                </div>
              ))}
          </section>
        </div>
      </div>
    </>
  );
}
